import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { productsService, ordersService } from '../../services';
import { ProductCategory } from '../../constants/productCategory';
import { Product } from '../../types/product.type';
import { useTranslation } from 'react-i18next';
import { VMConfig } from '../../types/vm.type';
import { vmInstanceTypes } from '../../constants/vmConstants';
import ProgressSteps from './components/ProgressSteps';
import Step1VM from './steps/Step1VM';
import Step2VM from './steps/Step2VM';
import Step3VM from './steps/Step3VM';

const CreateVM: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<VMConfig>({
    name: '',
    region: '',
    instanceType: '',
    os: '',
    storage: 60,
    backups: false,
    monitoring: true,
    needsPublicIP: true,
    publicIPType: 'dynamic',
    allowedIPs: '',
    exposedPorts: '',
    ipExpiration: '24h',
    understandsRisk: false,
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [selectedOSType, setSelectedOSType] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const hasFetchedProducts = useRef(false);

  useEffect(() => {
    if (hasFetchedProducts.current) return;
    hasFetchedProducts.current = true;

    const fetchProducts = async () => {
      try {
        const response = await productsService.getProductsByCategory(ProductCategory.VM);
        const productsArray = Array.isArray(response?.products) ? response.products : [];
        setProducts(productsArray);
      } catch (error) {
        console.error('Error fetching VM products:', error);
        setProducts([]);
        hasFetchedProducts.current = false;
      }
    };

    fetchProducts();
  }, []);

  const getOSIcon = (osfamily: string, productImages?: string[]): { type: 'image' | 'emoji'; value: string } => {
    if (productImages && productImages.length > 0) {
      return { type: 'image', value: productImages[0] };
    }

    const logoMap: Record<string, string> = {
      'amazon-linux': '/images/amazon linux.png',
      'ubuntu-22.04': '/images/ubuntu.png',
      'ubuntu-20.04': '/images/ubuntu.png',
      ubuntu: '/images/ubuntu.png',
      rhel: '/images/linux.png',
      redhat: '/images/linux.png',
      'red hat': '/images/linux.png',
      linux: '/images/linux.png',
      debian: '/images/debian.png',
      centos: '/images/linux.png',
      suse: '/images/suse.png',
      sles: '/images/suse.png',
      opensuse: '/images/suse.png',
      windows: '/images/windows.png',
      macos:
        'https://www.apple.com/ac/globalnav/7/en_US/images/be15095f-5a20-57d0-ad14-c6d1742a92cf/globalnav_apple_image__b5er5ng3xqq6_large.svg',
    };

    for (const [key, logoUrl] of Object.entries(logoMap)) {
      if (osfamily.toLowerCase().includes(key.toLowerCase())) {
        return { type: 'image', value: logoUrl };
      }
    }

    const emojiMap: Record<string, string> = {
      'amazon-linux': '☁️',
      ubuntu: '🟠',
      debian: '🌀',
      centos: '💠',
      windows: '🪟',
      rhel: '🔴',
      redhat: '🔴',
    };

    for (const [key, emoji] of Object.entries(emojiMap)) {
      if (osfamily.toLowerCase().includes(key.toLowerCase())) {
        return { type: 'emoji', value: emoji };
      }
    }

    return { type: 'emoji', value: '💻' };
  };

  const operatingSystems = useMemo(() => {
    if (!Array.isArray(products) || products.length === 0) return [];

    let osProducts = products.filter((product) => {
      const osFamily = product.osFamily;
      return osFamily && typeof osFamily === 'string';
    });

    if (selectedOSType) {
      osProducts = osProducts.filter(
        (product) => product.osType?.toLowerCase() === selectedOSType.toLowerCase()
      );
    }

    return osProducts.map((product) => {
      const osFamily = product.osFamily as string;
      const iconData = getOSIcon(osFamily, product.images);
      return {
        id: product.id,
        name: product.name,
        description: product.description,
        osFamily,
        osType: product.osType,
        icon: iconData.value,
        isImage: iconData.type === 'image',
        product,
      };
    });
  }, [products, selectedOSType]);

  const availableOSTypes = useMemo(() => {
    if (!Array.isArray(products) || products.length === 0) return [];

    const osProducts = products.filter((product) => product.osFamily && product.osType);
    const uniqueTypes = Array.from(
      new Set(
        osProducts
          .map((p) => p.osType?.toLowerCase())
          .filter((type): type is string => Boolean(type))
      )
    );
    const availableFilters: string[] = [...uniqueTypes];

    const hasAmazonLinux = products.some((p) => p.osFamily?.toLowerCase().includes('amazon-linux'));
    const hasUbuntu = products.some((p) => p.osFamily?.toLowerCase().includes('ubuntu'));
    if (hasAmazonLinux && !availableFilters.includes('amazon-linux')) availableFilters.push('amazon-linux');
    if (hasUbuntu && !availableFilters.includes('ubuntu')) availableFilters.push('ubuntu');

    return availableFilters;
  }, [products]);

  useEffect(() => {
    if (config.os && selectedOSType) {
      const selectedOS = operatingSystems.find((os) => os.id === config.os);
      if (!selectedOS || selectedOS.osType?.toLowerCase() !== selectedOSType.toLowerCase()) {
        setConfig((prev) => ({ ...prev, os: '' }));
      }
    }
  }, [selectedOSType, config.os, operatingSystems]);

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCreate = async () => {
    if (config.needsPublicIP && !config.publicIPType) {
      alert(t('virtualMachines.create.validation.publicIPRequired'));
      return;
    }
    if (!config.os || !config.instanceType) {
      console.error('Missing required configuration');
      return;
    }

    setIsCreating(true);
    try {
      const selectedProduct = products.find((p) => p.id === config.os);
      if (!selectedProduct) {
        console.error('Selected product not found');
        setIsCreating(false);
        return;
      }

      const instance = vmInstanceTypes.find((i) => i.id === config.instanceType);
      if (!instance) {
        console.error('Instance type not found');
        setIsCreating(false);
        return;
      }

      const orderData = {
        productId: selectedProduct.id,
        quantity: 1,
        metadata: {
          type: 'COMPUTE',
          vmConfig: {
            name: config.name,
            os: config.os,
            osName: selectedProduct.name,
            instanceType: config.instanceType,
            storage: config.storage,
            backups: config.backups,
            monitoring: config.monitoring,
            region: config.region,
            ...(config.needsPublicIP && {
              publicIP: { type: config.publicIPType },
            }),
          },
        },
      };

      const createdOrder = await ordersService.create(orderData);
      if (!createdOrder?.id) throw new Error('La API no devolvió el ID de la orden');
      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      alert(t('common.loading'));
      setIsCreating(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return Boolean(config.name && config.os);
      case 2:
        return Boolean(config.instanceType);
      case 3:
        return true;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Step1VM
            config={config}
            setConfig={setConfig}
            operatingSystems={operatingSystems}
            selectedOSType={selectedOSType}
            setSelectedOSType={setSelectedOSType}
            availableOSTypes={availableOSTypes}
            isLoading={products.length === 0}
          />
        );
      case 2:
        return <Step2VM config={config} setConfig={setConfig} />;
      case 3:
        return <Step3VM config={config} operatingSystems={operatingSystems} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6">
      <div className="mb-8">
        <button
          onClick={() => navigate('/virtual-machines')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftOutlined />
          <span className="text-sm font-medium">{t('common.back')}</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('virtualMachines.create.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">{t('virtualMachines.list.subtitle')}</p>
      </div>

      <ProgressSteps currentStep={step} />

      <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {renderStep()}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('virtualMachines.create.actions.back')}
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/virtual-machines')}
            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('virtualMachines.create.actions.cancel')}
          </button>
          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-6 py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('virtualMachines.create.actions.next')}
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating
                ? t('virtualMachines.create.actions.creating')
                : t('virtualMachines.create.actions.create')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateVM;
